/**
 * Reports Tabs Component
 * 
 * Consolidates all reporting and export functionality into a single tabbed interface:
 * - Reports: Main financial reports and analytics
 * - Export: Export to accounting systems
 * - Period Close: Period closing and reconciliation
 * 
 * This replaces the need for separate ExportCenter page.
 */

import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Download, Calendar, Filter, Plus, Search, BarChart3, PieChart, TrendingUp } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/hooks/use-toast";
import { CreateReportDialog } from "@/components/reports/create-report-dialog";
import { ExportWizard } from "@/components/export/export-wizard";
import { PreExportValidation } from "@/components/export/pre-export-validation";
import { ExportHistory } from "@/components/export/export-history";

interface ReportsTabsProps {
  className?: string;
}

export const ReportsTabs: React.FC<ReportsTabsProps> = ({ className }) => {
  const { organization } = useAuth();
  const { toast } = useToast();
  const [createReportOpen, setCreateReportOpen] = useState(false);

  // Fetch real exports data from database
  const { data: exports = [], isLoading, refetch } = useQuery({
    queryKey: ['exports', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      
      const { data, error } = await supabase
        .from('exports')
        .select('*')
        .eq('organization_id', organization.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!organization?.id
  });

  // Calculate statistics from real data
  const stats = {
    total: exports.length,
    thisMonth: exports.filter(exp => {
      const created = new Date(exp.created_at);
      const now = new Date();
      return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
    }).length,
    completed: exports.filter(exp => exp.status === 'completed').length,
    pending: exports.filter(exp => exp.status === 'pending' || exp.status === 'processing').length
  };

  const handleCreateReport = () => {
    setCreateReportOpen(true);
  };

  const handleExportComplete = () => {
    refetch();
    toast({
      title: "Export erfolgreich",
      description: "Die Daten wurden erfolgreich exportiert.",
    });
  };

  return (
    <div className={className}>
      <Tabs defaultValue="reports" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <i className="fa-solid fa-chart-bar"></i>
            Berichte
            {stats.total > 0 && (
              <Badge variant="secondary" className="ml-1">
                {stats.total}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="export" className="flex items-center gap-2">
            <i className="fa-solid fa-download"></i>
            Export
            {stats.pending > 0 && (
              <Badge variant="destructive" className="ml-1">
                {stats.pending}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="period-close" className="flex items-center gap-2">
            <i className="fa-solid fa-calendar-check"></i>
            Periodenabschluss
          </TabsTrigger>
        </TabsList>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-brand-text-secondary">Total Reports</p>
                    <p className="text-2xl font-bold text-brand-primary">{stats.total}</p>
                  </div>
                  <FileText className="h-8 w-8 text-brand-accent" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-brand-text-secondary">This Month</p>
                    <p className="text-2xl font-bold text-brand-primary">{stats.thisMonth}</p>
                  </div>
                  <Calendar className="h-8 w-8 text-brand-accent" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-brand-text-secondary">Completed</p>
                    <p className="text-2xl font-bold text-brand-success">{stats.completed}</p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-brand-success" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-brand-text-secondary">Pending</p>
                    <p className="text-2xl font-bold text-brand-warning">{stats.pending}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-brand-warning" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Reports List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <i className="fa-solid fa-chart-bar text-brand-primary"></i>
                  Finanzberichte
                </CardTitle>
                <Button onClick={handleCreateReport} className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Neuer Bericht
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center space-x-4">
                      <Skeleton className="h-12 w-12 rounded" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-[250px]" />
                        <Skeleton className="h-4 w-[200px]" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : exports.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-brand-text-secondary mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-brand-text-primary mb-2">Keine Berichte vorhanden</h3>
                  <p className="text-brand-text-secondary mb-4">Erstellen Sie Ihren ersten Finanzbericht.</p>
                  <Button onClick={handleCreateReport} className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Ersten Bericht erstellen
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {exports.map((exportItem: any) => (
                    <div key={exportItem.id} className="flex items-center justify-between p-4 border border-brand-border rounded-lg hover:bg-brand-secondary/50 transition-colors">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-brand-accent/20 rounded-lg flex items-center justify-center">
                          <FileText className="h-5 w-5 text-brand-accent" />
                        </div>
                        <div>
                          <h4 className="font-medium text-brand-text-primary">{exportItem.name || 'Unbenannter Bericht'}</h4>
                          <p className="text-sm text-brand-text-secondary">
                            Erstellt am {new Date(exportItem.created_at).toLocaleDateString('de-CH')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={exportItem.status === 'completed' ? 'default' : 'secondary'}>
                          {exportItem.status === 'completed' ? 'Abgeschlossen' : 'In Bearbeitung'}
                        </Badge>
                        <Button variant="outline" size="sm">
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <i className="fa-solid fa-download text-brand-primary"></i>
                Export zu Buchhaltungssystemen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ExportWizard />
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <i className="fa-solid fa-check-circle text-brand-primary"></i>
                  Pre-Export Validierung
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PreExportValidation />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <i className="fa-solid fa-history text-brand-primary"></i>
                  Export-Verlauf
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ExportHistory />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Period Close Tab */}
        <TabsContent value="period-close" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <i className="fa-solid fa-calendar-check text-brand-primary"></i>
                Periodenabschluss
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-brand-text-secondary mx-auto mb-4" />
                <h3 className="text-lg font-medium text-brand-text-primary mb-2">Periodenabschluss</h3>
                <p className="text-brand-text-secondary mb-4">
                  Funktionen für Periodenabschluss und Rechnungsabgrenzung werden hier verfügbar sein.
                </p>
                <Button disabled className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Bald verfügbar
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Report Dialog */}
      <CreateReportDialog
        open={createReportOpen}
        onOpenChange={setCreateReportOpen}
      />
    </div>
  );
};
